import React, { useCallback, useEffect, useReducer, useRef } from "react";

import classNames from "classnames";

import usePrevious from "../../hooks/usePrevious";

import style from "./TOTPCodeInput.module.css";

export type TOTPCodeInputProps = {
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  length?: number;
  className?: string;
  onBlur?: () => void;
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
};

const CODE_LENGTH = 6;

type TOTPState = {
  values: string[];
  focusedIndex: number;
};

type TOTPAction =
  | { type: "INSERT"; index: number; value: string }
  | { type: "DELETE"; index: number }
  | { type: "DELETE_PREVIOUS"; index: number }
  | { type: "PASTE"; index: number; value: string }
  | { type: "CLEAR_RANGE"; index: number; count: number; focusFirst?: boolean }
  | { type: "FOCUS"; index: number }
  | { type: "FOCUS_NEXT"; index: number }
  | { type: "FOCUS_PREV"; index: number }
  | { type: "FOCUS_FIRST" }
  | { type: "FOCUS_FIRST_EMPTY" }
  | { type: "RESET"; length: number; autoFocus: boolean };

const createInitialState = (length: number, autoFocus: boolean): TOTPState => ({
  values: Array(length).fill(""),
  focusedIndex: autoFocus ? 0 : -1,
});

const totpReducer = (state: TOTPState, action: TOTPAction): TOTPState => {
  const { values } = state;
  const length = values.length;

  switch (action.type) {
    case "INSERT": {
      const { index, value } = action;
      const digit = value.slice(-1).replace(/\D/g, "");
      if (!digit) return state;

      const newValues = [...values];
      newValues[index] = digit;

      return {
        values: newValues,
        focusedIndex: Math.min(index + 1, length - 1),
      };
    }

    case "DELETE": {
      const { index } = action;
      if (index < 0 || index >= length) return state;

      const newValues = [...values];
      newValues.splice(index, 1);
      newValues.push("");

      return {
        values: newValues,
        focusedIndex: index,
      };
    }

    case "DELETE_PREVIOUS": {
      const { index } = action;
      const newValues = [...values];

      let targetDeleteIndex = -1;
      let targetFocusIndex = index;

      if (values[index]) {
        targetDeleteIndex = index;
        targetFocusIndex = Math.max(0, index - 1);
      } else if (index > 0) {
        targetDeleteIndex = index - 1;
        targetFocusIndex = Math.max(0, index - 1);
      }

      if (targetDeleteIndex !== -1) {
        newValues.splice(targetDeleteIndex, 1);
        newValues.push("");
        return {
          values: newValues,
          focusedIndex: targetFocusIndex,
        };
      }

      return {
        ...state,
        focusedIndex: 0,
      };
    }

    case "PASTE": {
      const { index, value } = action;
      const digits = value.replace(/\D/g, "").split("");
      if (digits.length === 0) return state;

      const newValues = [...values];

      digits.forEach((digit, i) => {
        if (index + i < length) {
          newValues[index + i] = digit;
        }
      });

      const firstEmptyIndex = newValues.findIndex((v) => !v);
      const nextFocusIndex = firstEmptyIndex !== -1 ? firstEmptyIndex : length - 1;

      return {
        values: newValues,
        focusedIndex: nextFocusIndex,
      };
    }

    case "CLEAR_RANGE": {
      const { index, count } = action;
      const newValues = [...values];
      const end = Math.min(index + count, length);

      for (let i = index; i < end; i++) {
        newValues[i] = "";
      }

      const shiftedValues = Array(length).fill("");
      let writeIndex = 0;
      for (let i = 0; i < length; i++) {
        if (newValues[i]) {
          shiftedValues[writeIndex] = newValues[i];
          writeIndex++;
        }
      }
      const nextFocusIndex = action.focusFirst ? 0 : writeIndex < length ? writeIndex : length - 1;

      return {
        values: shiftedValues,
        focusedIndex: nextFocusIndex,
      };
    }

    case "FOCUS":
      return {
        ...state,
        focusedIndex: action.index,
      };

    case "FOCUS_NEXT":
      return {
        ...state,
        focusedIndex: Math.min(action.index + 1, length - 1),
      };

    case "FOCUS_PREV":
      return {
        ...state,
        focusedIndex: Math.max(action.index - 1, 0),
      };

    case "FOCUS_FIRST":
      return {
        ...state,
        focusedIndex: 0,
      };

    case "FOCUS_FIRST_EMPTY": {
      const firstEmptyIndex = values.findIndex((v) => !v);
      return {
        ...state,
        focusedIndex: firstEmptyIndex !== -1 ? firstEmptyIndex : length - 1,
      };
    }

    case "RESET":
      return createInitialState(action.length, action.autoFocus);

    default:
      return state;
  }
};

export const TOTPCodeInput = ({
  error = false,
  errorMessage,
  disabled = false,
  autoFocus = true,
  length = CODE_LENGTH,
  className,
  onBlur,
  onChange,
  onComplete,
}: TOTPCodeInputProps) => {
  const [state, dispatch] = useReducer(totpReducer, createInitialState(length, autoFocus));
  const { values, focusedIndex } = state;

  const previousValues = usePrevious(values);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (values.length !== length) {
      dispatch({ type: "RESET", length, autoFocus });
    }
  }, [length, values.length, autoFocus]);

  useEffect(() => {
    if (previousValues?.join("") === values.join("")) {
      return;
    }
    const code = values.join("");
    onChange?.(code);

    if (values.join("").length === length && values.every((v) => v !== "")) {
      onComplete?.(code);
    }
  }, [values, previousValues, length, onChange, onComplete]);

  useEffect(() => {
    if (disabled) return;
    if (focusedIndex >= 0 && focusedIndex < length) {
      const el = inputRefs.current[focusedIndex];
      if (el && document.activeElement !== el) {
        el.focus();
      }
    }
  }, [focusedIndex, disabled, length]);

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      const isModKey = e.metaKey || e.ctrlKey;

      if (isModKey && e.key === "ArrowLeft") {
        e.preventDefault();
        dispatch({ type: "FOCUS_FIRST" });
        return;
      }
      if (isModKey && e.key === "ArrowRight") {
        e.preventDefault();
        dispatch({ type: "FOCUS_FIRST_EMPTY" });
        return;
      }

      if (isModKey && e.key === "Delete") {
        e.preventDefault();
        dispatch({ type: "CLEAR_RANGE", index, count: length - index });
        return;
      }

      if (isModKey && e.key === "Backspace") {
        e.preventDefault();
        dispatch({ type: "CLEAR_RANGE", index: 0, count: index + 1, focusFirst: true });
        return;
      }

      if (e.key === "Delete" && !isModKey) {
        e.preventDefault();
        if (values[index]) {
          dispatch({ type: "DELETE", index });
        }
        return;
      }

      if (e.key === "Backspace" && !isModKey) {
        e.preventDefault();
        dispatch({ type: "DELETE_PREVIOUS", index });
        return;
      }

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        dispatch({ type: "FOCUS_PREV", index });
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (values[index]) {
          dispatch({ type: "FOCUS_NEXT", index });
        }
      }
    },
    [values, length]
  );

  const handlePaste = useCallback((index: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "");
    if (pastedData.length > 0) {
      dispatch({ type: "PASTE", index, value: pastedData });
    }
  }, []);

  const handleInputFocus = useCallback(
    (i: number, e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select();
      if (i !== focusedIndex) {
        dispatch({ type: "FOCUS", index: i });
      }
    },
    [focusedIndex]
  );

  const handleBlur = useCallback(() => {
    onBlur?.();
  }, [onBlur]);

  return (
    <div className={classNames(style.wrapper, className)}>
      <div className={style.inputContainer}>
        {Array.from({ length }).map((_, index) => (
          <React.Fragment key={index}>
            <input
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]"
              value={values[index] || ""}
              onChange={(e) => {
                const value = e.target.value;
                const digit = value.replace(/\D/g, "").slice(-1);

                if (digit) {
                  dispatch({ type: "INSERT", index, value: digit });
                } else if (value === "") {
                  dispatch({ type: "DELETE", index });
                }
              }}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={(e) => handlePaste(index, e)}
              onFocus={(e) => handleInputFocus(index, e)}
              onClick={() => {
                const firstEmptyIndex = values.findIndex((v) => !v);
                if (firstEmptyIndex !== -1 && index > firstEmptyIndex && !values[index]) {
                  dispatch({ type: "FOCUS", index: firstEmptyIndex });
                }
              }}
              onBlur={handleBlur}
              disabled={disabled}
              className={classNames(style.input, error && style.error)}
              aria-label={`Digit ${index + 1} of ${length}`}
            />
            {index === Math.floor(length / 2) - 1 && <span className={style.separator}>-</span>}
          </React.Fragment>
        ))}
      </div>
      {errorMessage && <span className={style.errorMessage}>{errorMessage}</span>}
    </div>
  );
};
